// Shared framer-motion primitives for the console. Centralising motion here
// keeps animations consistent and respects prefers-reduced-motion everywhere.
// Pages compose these instead of hand-rolling variants.

import { Children, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { motion, useReducedMotion, animate, type Variants } from "framer-motion";
import { useSplashDismiss } from "@/app/console/splash";

const EASE = [0.16, 1, 0.3, 1] as const;

// Motion band (docs/design/DESIGN_SYSTEM.md §1.9): entrances 120–180 ms, quick
// and functional. Page transition 180 ms · FadeUp 150 ms / 8 px · Stagger items
// 160 ms with a ≤40 ms step and ≤120 ms total stagger · Reveal 180 ms.
const PAGE_MS = 0.18;
const FADE_MS = 0.15;
const ITEM_MS = 0.16;
const STAGGER_STEP_MAX = 0.04;
const STAGGER_TOTAL_MAX = 0.12;

// ---------------------------------------------------------------------------
// Page-level transition. Mounted *inside* the route's Suspense boundary (see
// App.tsx LazyRoute) so the fade is tied to the real content, not the loading
// skeleton. Opacity-only by design: the per-page Stagger/FadeUp supply the
// vertical motion, so layering them reads as one coherent entrance instead of
// a fade-then-pop. Keep it quick.
// ---------------------------------------------------------------------------

export function PageTransition({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  // The first resolved screen dismisses the boot splash (index.html).
  useSplashDismiss();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: PAGE_MS, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Fade / slide single element
// ---------------------------------------------------------------------------

export function FadeUp({
  children,
  className,
  delay = 0,
  y = 8,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: FADE_MS, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Staggered container + items (grids, lists)
// ---------------------------------------------------------------------------

const staggerItem: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: ITEM_MS, ease: EASE } },
};

export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  // Step ≤40 ms, and the whole cascade finishes starting within 120 ms however
  // many children there are (7 KPI tiles → 20 ms step).
  const count = Children.count(children);
  const container = useMemo<Variants>(
    () => ({
      initial: {},
      animate: { transition: { staggerChildren: Math.min(STAGGER_STEP_MAX, STAGGER_TOTAL_MAX / Math.max(1, count - 1)) } },
    }),
    [count],
  );
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={container} initial="initial" animate="animate">
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={staggerItem}>
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Reveal on scroll into view
// ---------------------------------------------------------------------------

export function Reveal({
  children,
  className,
  y = 8,
}: {
  children: ReactNode;
  className?: string;
  y?: number;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: PAGE_MS, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Count-up animated number (for KPI tiles)
// ---------------------------------------------------------------------------

export function AnimatedNumber({
  value,
  format,
  duration = 0.9,
}: {
  value: number;
  format?: (v: number) => string;
  duration?: number;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  // Keep the latest formatter without retriggering the animation effect.
  const fmtRef = useRef(format);
  fmtRef.current = format;
  // The last numeric value actually painted. Tweening from here (rather than
  // always from 0) means a filter/period change glides old→new instead of
  // snapping every tile back to zero and re-counting — far smoother.
  const displayedRef = useRef(0);

  // Drive the count-up by writing textContent directly (no per-frame React
  // re-render) — critical when many KPI tiles animate at once.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const toText = (v: number) => {
      const f = fmtRef.current;
      return f ? f(v) : Math.round(v).toLocaleString("en-IN");
    };
    if (reduce) {
      displayedRef.current = value;
      node.textContent = toText(value);
      return;
    }
    const controls = animate(displayedRef.current, value, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => {
        displayedRef.current = v;
        node.textContent = toText(v);
      },
    });
    return () => controls.stop();
  }, [value, reduce, duration]);

  const initial = format ? format(value) : Math.round(value).toLocaleString("en-IN");
  return <span ref={ref}>{initial}</span>;
}

// ---------------------------------------------------------------------------
// Hover-lift wrapper for interactive cards
// ---------------------------------------------------------------------------

export function HoverLift({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
    >
      {children}
    </motion.div>
  );
}
